import { OTLayout } from "@/components/layout/ot-layout";
import { useListGuidelines } from "@workspace/api-client-react";
import { Link } from "wouter";
import { useState, useEffect } from "react";
import { Hand, ShieldCheck, Brain, Users, Cpu, BarChart3, BookOpen, MessageSquare, ExternalLink, GraduationCap, Shield, TrendingUp, Loader2 } from "lucide-react";

interface CpdSummary {
  count: number;
  totalHours: number;
  byCategory: Record<string, number>;
}

function CpdDashboardWidget() {
  const [summary, setSummary] = useState<CpdSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [renewalDate, setRenewalDate] = useState<string | null>(null);

  useEffect(() => {
    fetch("/api/ot/cpd/summary").then(r => r.ok ? r.json() : null).then(d => {
      if (d) setSummary(d);
      setLoading(false);
    }).catch(() => setLoading(false));
    fetch("/api/ot/settings").then(r => r.ok ? r.json() : null).then(d => {
      if (d?.hcpcRenewalDate) setRenewalDate(d.hcpcRenewalDate);
    });
  }, []);

  const daysUntilRenewal = renewalDate
    ? Math.ceil((new Date(renewalDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  const renewalColour = daysUntilRenewal === null ? "text-slate-400"
    : daysUntilRenewal < 0 ? "text-red-600"
    : daysUntilRenewal < 90 ? "text-orange-600"
    : daysUntilRenewal < 180 ? "text-amber-600"
    : "text-emerald-600";

  return (
    <div className="bg-white rounded-2xl border border-amber-200 p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <GraduationCap className="h-5 w-5 text-amber-600" />
          <h3 className="font-bold text-slate-900">CPD Portfolio</h3>
        </div>
        <Link href="/ot/cpd">
          <span className="text-xs text-amber-700 hover:text-amber-800 font-medium cursor-pointer">Open CPD Log →</span>
        </Link>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Loading CPD data…</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-amber-50 rounded-xl p-3">
            <p className="text-2xl font-bold text-amber-700">{summary?.count ?? 0}</p>
            <p className="text-xs text-slate-500">CPD activities this year</p>
          </div>
          <div className="bg-orange-50 rounded-xl p-3">
            <p className="text-2xl font-bold text-orange-700">{(summary?.totalHours ?? 0).toFixed(1)}</p>
            <p className="text-xs text-slate-500">Total CPD hours</p>
          </div>
          <Link href="/ot/cpd/revalidation">
            <div className="bg-slate-50 rounded-xl p-3 cursor-pointer hover:bg-slate-100 transition-colors col-span-2">
              <div className="flex items-center gap-2 mb-1">
                <Shield className="h-4 w-4 text-amber-500" />
                <p className="text-xs font-medium text-slate-600">HCPC Renewal</p>
              </div>
              {daysUntilRenewal !== null ? (
                <p className={`text-sm font-bold ${renewalColour}`}>
                  {daysUntilRenewal < 0 ? `${Math.abs(daysUntilRenewal)}d overdue` : `${daysUntilRenewal} days remaining`}
                </p>
              ) : (
                <p className="text-xs text-slate-400">Set renewal date →</p>
              )}
            </div>
          </Link>
        </div>
      )}
      <div className="flex flex-wrap gap-2 mt-4">
        <Link href="/ot/cpd/rcot-framework">
          <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-3 py-1.5 rounded-full hover:bg-amber-100 cursor-pointer transition-colors">RCOT Framework</span>
        </Link>
        <Link href="/ot/cpd/advanced-practice">
          <span className="text-xs bg-orange-50 text-orange-700 border border-orange-200 px-3 py-1.5 rounded-full hover:bg-orange-100 cursor-pointer transition-colors">Advanced Practice Portfolio</span>
        </Link>
        <Link href="/ot/cpd/reflection">
          <span className="text-xs bg-slate-50 text-slate-600 border border-slate-200 px-3 py-1.5 rounded-full hover:bg-slate-100 cursor-pointer transition-colors">Reflection Tool</span>
        </Link>
        <Link href="/ot/career">
          <span className="text-xs bg-rose-50 text-rose-700 border border-rose-200 px-3 py-1.5 rounded-full hover:bg-rose-100 cursor-pointer transition-colors flex items-center gap-1"><TrendingUp className="h-3 w-3" />Career Pathways</span>
        </Link>
      </div>
    </div>
  );
}

const quickAccessCards = [
  {
    href: "/ot/hand-assessment",
    icon: Hand,
    label: "Hand Assessment & Splinting",
    description: "Deformity classification, grip assessment, splinting protocols",
    color: "bg-amber-50 text-amber-700",
    hoverBorder: "hover:border-amber-300",
  },
  {
    href: "/ot/joint-protection",
    icon: ShieldCheck,
    label: "Joint Protection",
    description: "JPPE principles, condition-specific adaptations, patient education",
    color: "bg-orange-50 text-orange-700",
    hoverBorder: "hover:border-orange-300",
  },
  {
    href: "/ot/fatigue-management",
    icon: Brain,
    label: "Fatigue Management",
    description: "COT programme, pacing, energy conservation, condition profiles",
    color: "bg-yellow-50 text-yellow-700",
    hoverBorder: "hover:border-yellow-300",
  },
  {
    href: "/ot/adl",
    icon: Users,
    label: "ADL & Functional Independence",
    description: "Personal care, home management, COPM guidance, work participation",
    color: "bg-lime-50 text-lime-700",
    hoverBorder: "hover:border-lime-300",
  },
  {
    href: "/ot/assistive-technology",
    icon: Cpu,
    label: "Assistive Technology",
    description: "Category browser, prescription principles, Living Made Easy",
    color: "bg-emerald-50 text-emerald-700",
    hoverBorder: "hover:border-emerald-300",
  },
  {
    href: "/ot/outcome-measures",
    icon: BarChart3,
    label: "Outcome Measures",
    description: "COPM & DASH calculators, HAQ-DI, ABILHAND, EQ-5D, AIMS2",
    color: "bg-teal-50 text-teal-700",
    hoverBorder: "hover:border-teal-300",
  },
];

export default function OTDashboard() {
  const { data: guidelines, isLoading } = useListGuidelines();

  return (
    <OTLayout title="OT Dashboard">
      <div className="space-y-8">

        {/* Welcome */}
        <div className="bg-gradient-to-br from-amber-700 to-orange-600 rounded-3xl p-8 text-white">
          <div className="flex items-center gap-3 mb-3">
            <Hand className="h-8 w-8 text-amber-200" />
            <h2 className="text-2xl font-bold">Rheumatology OT Portal</h2>
          </div>
          <p className="text-amber-100 max-w-2xl">
            Evidence-based clinical tools for rheumatology occupational therapists — grounded in RCOT Code of Ethics, NICE guidelines, and COT specialist section guidance.
          </p>
        </div>

        {/* CPD Portfolio Widget */}
        <CpdDashboardWidget />

        {/* Quick Access */}
        <div>
          <h3 className="text-lg font-bold text-slate-900 mb-4">Clinical Tools</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {quickAccessCards.map((card) => (
              <Link key={card.href} href={card.href}>
                <div className={`bg-white rounded-2xl border border-slate-200 p-5 ${card.hoverBorder} hover:shadow-md transition-all cursor-pointer h-full`}>
                  <div className={`h-10 w-10 rounded-xl ${card.color} flex items-center justify-center mb-3`}>
                    <card.icon className="h-5 w-5" />
                  </div>
                  <h4 className="font-semibold text-slate-900 mb-1 text-sm">{card.label}</h4>
                  <p className="text-xs text-slate-500 leading-relaxed">{card.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* AI Assistant prompt */}
        <Link href="/ot/chat">
          <div className="bg-white rounded-2xl border border-slate-200 hover:border-amber-300 hover:shadow-md transition-all p-6 cursor-pointer">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-2xl bg-amber-50 text-amber-700 flex items-center justify-center">
                <MessageSquare className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900">OT AI Clinical Assistant</h3>
                <p className="text-sm text-slate-500 mt-0.5">Grounded in RCOT Code, NICE guidelines, COT specialist guidance, and evidence-based rheumatology OT practice.</p>
              </div>
            </div>
          </div>
        </Link>

        {/* Guidelines preview */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-slate-900">Guidelines</h3>
            <Link href="/ot/guidelines" className="text-sm text-amber-700 hover:text-amber-800 font-medium flex items-center gap-1">
              View all <ExternalLink className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
            {isLoading ? (
              <div className="p-6 text-slate-400 text-sm">Loading guidelines…</div>
            ) : guidelines && guidelines.length > 0 ? (
              guidelines.slice(0, 4).map((g) => (
                <div key={g.id} className="px-6 py-4 flex items-start gap-3">
                  <BookOpen className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-slate-900">{g.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{g.organisation} · {g.category}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-6 text-slate-400 text-sm">See the Guidelines page for RCOT, COT, and NICE resources.</div>
            )}
          </div>
        </div>

      </div>
    </OTLayout>
  );
}
